import React, { useState } from 'react';
import {
    Card,
    Tabs,
    Button,
    Space,
    Typography,
    Input,
    DatePicker,
    Select,
    Form
} from 'antd';
import {
    ReloadOutlined,
    FilterOutlined,
    SearchOutlined
} from '@ant-design/icons';
import RequestList from '../components/requests/RequestList';
import { useRequests } from '../hooks/useRequests';
import type { ActionFlowRequestSearch } from '../types/request';

const { Title } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

export const RequestHistoryView: React.FC = () => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('approved');
    const [searchParams, setSearchParams] = useState<Omit<ActionFlowRequestSearch, 'status'>>({
        numberOfRows: 40,
        sortBy: 'scopeDate',
        orderBy: 'desc'
    });

    const { requests, isLoading, error, refetch } = useRequests({
        ...searchParams,
        // @ts-ignore
        status: activeTab.toUpperCase()
    });

    const handleSearch = (values: any) => {
        setSearchParams((prev) => ({
            ...prev,
            ...values,
            dateRange: values.dateRange
                ? [values.dateRange[0].format('YYYY-MM-DD'), values.dateRange[1].format('YYYY-MM-DD')]
                : undefined
        }));
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({ numberOfRows: 40, sortBy: 'scopeDate', orderBy: 'desc' });
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={2}>Request History</Title>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
                    Refresh
                </Button>
            </div>

            <Card>
                <Form form={form} layout="vertical" onFinish={handleSearch} className="mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Form.Item name="q" label="Search">
                            <Input prefix={<SearchOutlined />} placeholder="Search records..." allowClear />
                        </Form.Item>
                        <Form.Item name="domain" label="Domain">
                            <Select
                                placeholder="Select domain"
                                allowClear
                                options={[
                                    { label: 'Loans', value: 'LOANS' },
                                    { label: 'Accounts', value: 'ACCOUNTS' },
                                    { label: 'Customers', value: 'CUSTOMERS' }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="dateRange" label="Date Range">
                            <RangePicker style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item label=" " className="flex justify-end">
                            <Space>
                                <Button onClick={handleReset}>Reset</Button>
                                <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                                    Filter
                                </Button>
                            </Space>
                        </Form.Item>
                    </div>
                </Form>

                <Tabs activeKey={activeTab} onChange={handleTabChange} className="mb-4">
                    <TabPane tab="Pending" key="pending" />
                    <TabPane tab="Approved" key="approved" />
                    <TabPane tab="Rejected" key="rejected" />
                    <TabPane tab="Returned" key="returned" />
                    <TabPane tab="Completed" key="completed" />
                </Tabs>

                <RequestList
                    requests={requests}
                    isLoading={isLoading}
                    error={error as Error | null}
                    showResendAction={activeTab === 'completed' || activeTab === 'approved'}
                />
            </Card>
        </div>
    );
};

export default RequestHistoryView;
